import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { ResultRow } from './results-row';

import { cancelFulfillments } from '../../fulfillments.api';
import { FULFILLMENTS_CANCELLED } from '../../../../services/pusher-service/pusher-contract-messages';
import { usePusherEvent } from '../../../../hooks/use-pusher-event';
import { emptyArray } from 'ui-components/default-props/empty';
import { messages } from './messages';
import style from './style.module.less';

const modalStates = {
  idle: 'idle',
  requesting: 'requesting',
  hasResults: 'hasResults',
};

export const CancelFulfillmentModal = ({ fulfillmentPlanIds = emptyArray, onClose }) => {
  const [modalState, setModalState] = useAsyncSafeState(modalStates.idle);
  const [cancelResults, setCancelResults] = useAsyncSafeState(emptyArray);
  const onClick = useCallback(() => {
    setModalState(modalStates.requesting);
    cancelFulfillments(fulfillmentPlanIds)
      .then(() => {
        /* success depends on pusher */
      })
      .catch(() => {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.requestError} />,
          duration: 3000,
        });
      });
  }, [fulfillmentPlanIds, setModalState]);

  const onPusherMessage = useCallback(
    (pusherMessage) => {
      const { results } = pusherMessage;

      setModalState(modalStates.hasResults);
      if (!results) {
        return;
      }

      setCancelResults(
        results.map(({ FillID, OrderNumber, Success, Message }) => ({
          fulfillmentId: FillID,
          orderNumber: OrderNumber,
          isCancelled: Success,
          errorMessage: Message,
        })),
      );
    },
    [setModalState, setCancelResults],
  );

  usePusherEvent(FULFILLMENTS_CANCELLED, onPusherMessage);

  const count = fulfillmentPlanIds.length;

  return (
    <Modal className={style.modal}>
      <ModalHeader onClose={onClose}>
        <FormattedMessage {...messages.cancelFulfillments} values={{ count }} />
      </ModalHeader>
      <ModalBody className={style.body}>
        {modalState === modalStates.idle && (
          <>
            <div className={style.explanation}>
              <FormattedMessage {...messages.cancelFulfillmentBlurb} values={{ count }} />
            </div>
            <div className={style.confirmation}>
              <FormattedMessage {...messages.cancelFulfillmentConfirm} values={{ count }} />
            </div>
          </>
        )}
        {modalState === modalStates.requesting && <LoadingIndicator className={style['loading-indicator']} />}
        {modalState === modalStates.hasResults && (
          <>
            <div className={style['results-header']}>
              <FormattedMessage {...messages.resultsHeader} />
            </div>
            <div className={style.grid}>
              <div className={classnames(style.header, style.cell)}>
                <FormattedMessage {...messages.orderNumber} />
              </div>
              <div className={classnames(style.header, style.cell)}>
                <FormattedMessage {...messages.result} />
              </div>
              {cancelResults.map((result) => (
                <ResultRow key={result.fulfillmentId} isCancelled={result.isCancelled} orderNumber={result.orderNumber} />
              ))}
            </div>
          </>
        )}
      </ModalBody>
      {modalState === modalStates.idle && (
        <ModalFooter className={style.footer}>
          <ModalFooterCancelButton closeModal={onClose} text={messages.dontCancelFulfillment} />
          <Button onClick={onClick} theme='primary'>
            <FormattedMessage {...messages.continueButton} />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

CancelFulfillmentModal.propTypes = {
  fulfillmentPlanIds: PropTypes.array,
  onClose: PropTypes.func.isRequired,
};
